import {
  all,
  call,
  cancel,
  debounce,
  delay,
  fork,
  put,
  retry,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import {
  imActions,
  imAsPeerIdSelector,
  imCallSelector,
  imChatInfoSelector,
  imHistorySelector,
  imSelector,
  imTelegramSelector,
} from '$store/im/index';
import { toastActions } from '$store/toast';
import { aiCapsActions } from '$store/aiCaps';
import { Api } from '$api';
import { mainActions, mainSelector } from '$store/main';
import {
  BroadcastFilterName,
  BuyMessageAction,
  CallAction,
  CallCancelAction,
  CallSendAcceptAction,
  CallStatus,
  CallType,
  CreateInvoiceAction,
  DeleteDialogAction,
  DeleteMessageAction,
  EditWelcomeMessageAction,
  ImState,
  IncomingCallAction,
  LoadAvgInvoicePriceAction,
  LoadBroadcastStats,
  LoadChatAction,
  LoadDialogsAction,
  LoadMoreDialogsAction,
  LoadMoreHistoryAction,
  LoadWelcomeMessageAction,
  ReadHistoryAction,
  ReportMessageAction,
  RetrySendAction,
  SendInstantMessageAction,
  SendMessageAction,
  SendTipsAction,
  SendTypingAction,
  SendValentineDayStickerAction,
  SetAsPeerIdAction,
  SetTypingAction,
  TelegramRetryAction,
  UnsendBroadcastAction,
} from '$store/im/interface';
import { RootState } from '$store/rootReducer';
import {
  attachActions,
  attachFullItemsSelector,
  attachItemsSelector,
} from '$store/attach';
import {
  AuthPopoverState,
  CurrencyEnum,
  MessageModel,
  MessageTelegramStatus,
} from '$store/models';
import { PayloadAction } from '@reduxjs/toolkit';
import { authSelector } from '$store/auth';
import {
  _confirm,
  ATTACHMENTS_COUNT_LIMIT,
  formatCurrency,
  scrollToBottom,
  statReachGoal,
} from '$utils';
import { batchActions } from '$store';
import { blacklistActions } from '$store/blacklist';
import { goBack, goTo, hideModal, showModal } from '$navigation/helper';
import router from '$navigation/router';
import { t } from '$translation/helper';
import { CHAT, IM, MINE, PAY_FORM } from '$shared/constants/pages';
import { paySaga } from '$store/pay/sagas';
import { PayMethod } from '$store/pay/interface';
import { payActions } from '$store/pay';
import { followersActions } from '$store/followers';
import { balanceActions } from '$store/balance';
import { handleProfileResult } from '$store/profile/sagas';
import { profileActions } from '$store/profile';
import { v7 as uuidv7 } from 'uuid';
import {
  saveUnsentMessage,
  removeUnsentMessage,
  getUnsentMessages,
  getMinUnsentMessageId,
} from '$utils/unsentMessages';

let MsgId = 0;

const SEND_MAX_ATTEMPTS = 5;
const SEND_TIMEOUT = 12000;
const SEND_RETRY_DELAY = 1500;
const STATUS_KEYS_CHUNK = 200;

function isRetryableSendError(e: any) {
  return Boolean(e?.isNetworkError || e?.serverError);
}

function* sendMessageRequest(url: string, body: any, idempotencyKey?: string) {
  const config = {
    timeout: SEND_TIMEOUT,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  };
  for (let attempt = 1; ; attempt++) {
    try {
      return yield call(Api.post, url, body, config);
    } catch (e) {
      if (attempt >= SEND_MAX_ATTEMPTS || !isRetryableSendError(e)) {
        throw e;
      }
      yield delay(SEND_RETRY_DELAY * attempt);
    }
  }
}

function* finalizeSentMessage(peerId: number, message: MessageModel, data: any) {
  const { currentUserId } = yield select(authSelector);
  removeUnsentMessage(currentUserId, peerId, message.id);

  if (data?.message) {
    yield put(
      imActions.setMessage({
        peerId,
        messageId: message.id,
        message: { ...data.message, replyTo: data.message.replyTo ?? message.replyTo },
      }),
    );
    return;
  }

  const history = yield select(imHistorySelector, peerId);
  const current = history?.[message.id];
  if (current?.isSending) {
    yield put(
      imActions.setMessage({
        peerId,
        messageId: message.id,
        message: { ...current, isSending: false, isFailed: false },
      }),
    );
  }
}

function withAs(params: any, asPeerId?: number) {
  return asPeerId ? { ...params, asPeerId } : params;
}

function urlWithAs(path: string, asPeerId?: number) {
  if (!asPeerId) return path;
  return `${path}${path.includes('?') ? '&' : '?'}asPeerId=${asPeerId}`;
}

function* loadServiceAccountsWorker() {
  try {
    const data = yield call(Api.get, '/admin/im/service-accounts');
    const accounts: number[] = data.accounts || [];
    yield put(
      batchActions(
        mainActions.setUsers(data.users || {}),
        imActions.setServiceAccounts(accounts),
      ),
    );
    const { asPeerId } = yield select(imSelector);
    if (asPeerId && !accounts.includes(asPeerId)) {
      yield put(imActions.resetAsPeerId());
    }
  } catch (e) {
    yield put(imActions.setServiceAccounts([]));
    const { asPeerId } = yield select(imSelector);
    if (asPeerId) {
      yield put(imActions.resetAsPeerId());
    }
  }
}

function* loadDialogsWorker(action: LoadDialogsAction) {
  try {
    const { params } = yield call(router.getState);
    const section = params.section || 'all';
    const asPeerId = yield select(imAsPeerIdSelector);
    const data = yield retry(
      Infinity,
      3000,
      Api.post,
      '/im',
      withAs({ startFrom: 0, section }, asPeerId || undefined),
    );

    const currentAsPeerId = yield select(imAsPeerIdSelector);
    if ((asPeerId || 0) !== (currentAsPeerId || 0)) return;

    const messages: { [index: number]: MessageModel } = {};
    for (const message of data.messages) {
      messages[message.id] = message;
    }

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        imActions.setDialogsList(data.dialogs.map((item) => item.peerId)),
        imActions.setDialogs({
          dialogs: data.dialogs,
          messages,
          nextFrom: data.nextFrom,
          section,
          suggestions: data.suggestions,
        }),
        mainActions.setNewDialogs(data.newDialogs),
        followersActions.setFollowing(data.followed || {}),
        imActions.setHasWelcomeMessage(data.hasWelcomeMessage),
        imActions.setUnAnsweredCount(data.unAnsweredCount),
        imActions.setLists(data.lists || []),
        aiCapsActions.set({ suggest: !!data.aiCaps?.suggest }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMoreDialogsWorker(action: LoadMoreDialogsAction) {
  try {
    const { params } = yield call(router.getState);
    const section = params.section || 'all';
    const { nextFrom } = action.payload;
    const asPeerId = yield select(imAsPeerIdSelector);
    const data = yield call(
      Api.post,
      '/im',
      withAs({ startFrom: nextFrom, section }, asPeerId || undefined),
    );

    const currentAsPeerId = yield select(imAsPeerIdSelector);
    if ((asPeerId || 0) !== (currentAsPeerId || 0)) return;

    const messages: { [index: number]: MessageModel } = {};
    for (const message of data.messages) {
      messages[message.id] = message;
    }

    yield put(
      batchActions(
        imActions.addDialogsList(data.dialogs.map((item) => item.peerId)),
        mainActions.setUsers(data.users),
        imActions.setDialogs({
          dialogs: data.dialogs,
          messages,
          nextFrom: data.nextFrom,
        }),
        mainActions.setNewDialogs(data.newDialogs),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* sendMessageWorker(action: SendMessageAction) {
  const messageId = --MsgId;
  const { peerId, asPeerId } = action.payload;

  try {
    const { currentUserId } = yield select(authSelector);
    const info = yield select((state: RootState) => imChatInfoSelector(state, peerId));
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, `im_${peerId}`),
    );
    const text = info.text || '';

    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (text.length === 0 && !attachments.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    const attachmentsFull = yield select((state: RootState) =>
      attachFullItemsSelector(state, `invoice_${peerId}`),
    );

    if (attachments.length > ATTACHMENTS_COUNT_LIMIT) {
      return yield put(toastActions.setToastFail(t('photos_limit_error')));
    }

    const message: MessageModel = {
      id: messageId,
      fromId: asPeerId || currentUserId,
      toId: peerId,
      text,
      attachments: attachmentsFull,
      createdAt: Math.floor(Date.now() / 1000),
      isSending: true,
      isInbox: false,
      isUnread: true,
      idempotencyKey: uuidv7(),
      replyToId: info.replyTo?.id,
      replyTo: info.replyTo || undefined,
    };

    yield put(
      batchActions(
        imActions.chatSetFormText({
          peerId,
          text: '',
        }),
        imActions.chatSetReplyTo({
          peerId,
          replyTo: null,
        }),
        attachActions.clear(`im_${peerId}`),
        imActions.setMessage({
          peerId,
          messageId: message.id,
          message,
        }),
        imActions.setAIOptions([]),
      ),
    );

    yield call(scrollToBottom, 'chat_history');

    saveUnsentMessage(currentUserId, peerId, message);

    yield call(statReachGoal, 'start_send_message');

    const data = yield call(
      sendMessageRequest,
      `/im/${peerId}/send`,
      withAs(
        {
          text,
          attachments,
          randomId: message.id,
          replyToId: message.replyToId,
        },
        asPeerId,
      ),
      message.idempotencyKey,
    );

    yield call(finalizeSentMessage, peerId, message, data);

    yield call(statReachGoal, 'message');
  } catch (e) {
    if (e.code === 'subscription_required') {
      yield put(
        imActions.setShowSubscriptionRequiredMessage({
          peerId,
          show: true,
        }),
      );
    } else {
      yield put(toastActions.setToastFail(e.message));
    }

    const history = yield select(imHistorySelector, peerId);
    if (history[messageId]) {
      const message = { ...history[messageId], isFailed: true, isSending: false };
      yield put(
        imActions.setMessage({
          peerId,
          messageId,
          message,
        }),
      );

      const { currentUserId } = yield select(authSelector);
      saveUnsentMessage(currentUserId, peerId, message);
    }
  }
}

function* retrySendWorker(action: RetrySendAction) {
  const { peerId, message, asPeerId } = action.payload;

  try {
    yield put(
      imActions.setMessage({
        peerId,
        messageId: message.id,
        message: {
          ...message,
          isFailed: false,
          isSending: true,
        },
      }),
    );

    const data = yield call(
      sendMessageRequest,
      `/im/${peerId}/send`,
      withAs(
        {
          text: message.text,
          attachments: message.attachments,
          randomId: message.id,
          replyToId: message.replyToId,
        },
        asPeerId,
      ),
      message.idempotencyKey || uuidv7(),
    );

    yield call(finalizeSentMessage, peerId, message, data);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));

    const failedMessage = { ...message, isFailed: true, isSending: false };
    yield put(
      imActions.setMessage({
        peerId,
        messageId: message.id,
        message: failedMessage,
      }),
    );

    const { currentUserId } = yield select(authSelector);
    saveUnsentMessage(currentUserId, peerId, failedMessage);
  }
}

function* restoreUnsentMessages(peerId: number, asPeerId?: number) {
  const { currentUserId } = yield select(authSelector);
  const unsentMessages = getUnsentMessages(currentUserId, peerId);
  const ids = Object.keys(unsentMessages);
  if (!ids.length) {
    return;
  }

  MsgId = Math.min(MsgId, getMinUnsentMessageId(currentUserId));

  for (let i = 0; i < ids.length; i += STATUS_KEYS_CHUNK) {
    const chunkIds = ids.slice(i, i + STATUS_KEYS_CHUNK);
    const keys = chunkIds.map((id) => unsentMessages[id].idempotencyKey).filter(Boolean);

    let delivered: Record<string, number> = {};
    if (keys.length) {
      try {
        const data = yield call(
          Api.post,
          urlWithAs(`/im/${peerId}/status`, asPeerId),
          { keys },
          { timeout: SEND_TIMEOUT },
        );
        delivered = data?.items || {};
      } catch (e) {}
    }

    for (const id of chunkIds) {
      const message = unsentMessages[id];
      if (message.idempotencyKey && delivered[message.idempotencyKey]) {
        removeUnsentMessage(currentUserId, peerId, Number(id));
      } else {
        yield put(
          imActions.setMessage({
            peerId,
            messageId: Number(id),
            message: { ...message, isSending: false, isFailed: true },
          }),
        );
      }
    }
  }
}

function* loadChatWorker(action: LoadChatAction) {
  const { peerId } = action.payload;
  const asPeerId = yield select(imAsPeerIdSelector);

  try {
    const data = yield call(
      Api.post,
      urlWithAs(`/im/${peerId}/history`, asPeerId || undefined),
      { startFrom: 0 },
    );

    const currentAsPeerId = yield select(imAsPeerIdSelector);
    if ((asPeerId || 0) !== (currentAsPeerId || 0)) return;

    const history: { [index: number]: MessageModel } = {};
    for (const message of data.history) {
      history[message.id] = message;
    }

    yield put(
      batchActions(
        mainActions.setUser({ ...data.user, isSponsor: data.sponsor }),
        imActions.setHistory({
          peerId,
          history,
          nextFrom: data.nextFrom,
          spendLevel: data.spendLevel,
          replace: true,
        }),
        aiCapsActions.set({ suggest: !!data.aiCaps?.suggest }),
        blacklistActions.setBanFromMe({
          userId: peerId,
          isBanned: data.banFromMe,
        }),
        blacklistActions.setBanToMe({
          userId: peerId,
          isBanned: data.banToMe || data.isBotStopped,
        }),
        imActions.setAIOptions([]),
      ),
    );

    yield call(restoreUnsentMessages, peerId, asPeerId || undefined);

    yield call(scrollToBottom, 'chat_history');
    yield put(imActions.readHistory({ peerId, asPeerId: asPeerId || undefined }));
  } catch (e) {
    yield put(imActions.setChatFailed({ peerId, reason: e.message }));
  }
}

function* loadMoreHistoryWorker(action: LoadMoreHistoryAction) {
  const { peerId, nextFrom } = action.payload;
  const asPeerId = yield select(imAsPeerIdSelector);

  try {
    const data = yield call(
      Api.post,
      urlWithAs(`/im/${peerId}/history`, asPeerId || undefined),
      { startFrom: nextFrom },
    );

    const currentAsPeerId = yield select(imAsPeerIdSelector);
    if ((asPeerId || 0) !== (currentAsPeerId || 0)) return;

    const history: { [index: number]: MessageModel } = {};
    for (const message of data.history) {
      history[message.id] = message;
    }

    yield put(
      imActions.setHistory({
        peerId,
        history,
        nextFrom: data.nextFrom,
        spendLevel: data.spendLevel,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* readHistoryWorker(action: ReadHistoryAction) {
  try {
    const { peerId, asPeerId } = action.payload;
    yield call(Api.post, `/im/${peerId}/read`, asPeerId ? { asPeerId } : undefined);

    const { dialogs } = yield select(imSelector);
    if (dialogs[peerId]) {
      yield put(
        imActions.setDialogs({
          dialogs: [{ ...dialogs[peerId], unread: 0 }],
          messages: {},
        }),
      );
    }
  } catch (e) {}
}

function* loadWriteWorker(action: PayloadAction<{ asPeerId?: number } | undefined>) {
  try {
    const query = router.getState().params?.query ?? '';
    const asPeerId = action.payload?.asPeerId;
    const data = yield retry(
      Infinity,
      3000,
      Api.post,
      '/im/write',
      withAs({ query }, asPeerId),
    );
    yield put(
      batchActions(mainActions.setUsers(data.users), imActions.setWriteIds(data.items)),
    );
  } catch (e) {}
}

function* deleteDialogWorker(action: DeleteDialogAction) {
  try {
    const { peerId, asPeerId } = action.payload;
    const isConfirmed = yield call(_confirm, t('im_delete_confirmation'));
    if (!isConfirmed) {
      return;
    }

    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/im/${peerId}/delete`, asPeerId ? { asPeerId } : undefined);
    yield put(imActions.removeDialog(peerId));
    yield put(toastActions.setToastSuccess());
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* deleteMessageWorker(action: DeleteMessageAction) {
  try {
    const { id, peerId, asPeerId } = action.payload;
    const isConfirmed = yield call(_confirm, t('im_delete_message_confirmation'));
    if (!isConfirmed) {
      return;
    }

    yield put(toastActions.setToastLoading());
    yield call(Api.post, urlWithAs(`/im/message/${id}/delete`, asPeerId));
    yield put(imActions.removeMessage({ id, peerId }));
    yield put(toastActions.hideToast());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* deleteUnsentMessageWorker(action: DeleteMessageAction) {
  const { id, peerId } = action.payload;
  const { currentUserId } = yield select(authSelector);
  removeUnsentMessage(currentUserId, peerId, id);
  yield put(imActions.removeMessage({ id, peerId }));
}

function* reportMessageWorker(action: ReportMessageAction) {
  const { messageId, reason } = action.payload;
  try {
    yield call(Api.post, `/report`, {
      reason,
      objectId: messageId,
      objectType: 'message',
    });
    yield put(toastActions.setToastSuccess(t('message_report_sent')));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* createTelegramInvoiceWorker() {
  const { telegram, forms } = (yield select(imSelector)) as ImState;
  const formName = 'tg';
  const form = forms[formName];

  const attachments = yield select((state: RootState) =>
    attachItemsSelector(state, `tg_invoice`),
  );

  if (attachments === 'photo_uploading') {
    return yield put(toastActions.setToastFail(t('photo_uploading_err')));
  }

  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/im/telegram/post`, {
      price: parseFloat(form.price),
      message: form.message,
      chatIds: telegram.chatIds,
      attachments: attachments,
      lang: form.lang || 'en',
      disableNotifications: false,
    });
    yield put(toastActions.setToastSuccess());
    yield put(imActions.setTelegramSheetVisible(false));
    yield put(imActions.setChooseChatTelegramSheetVisible(false));
    yield put(imActions.clearForm(formName));
    yield put(attachActions.clear(formName));
    yield call(hideModal);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* createInvoiceWorker(action: CreateInvoiceAction) {
  const messageId = --MsgId;
  const { price, priceWithoutDiscount, peerId, text, currency } = action.payload;

  const attachId = `invoice_${peerId}`;

  try {
    const { currentUserId } = yield select(authSelector);
    const { mVerified } = yield select(mainSelector);

    yield put(toastActions.setToastLoading());
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, attachId),
    );

    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (!attachments.length && !text.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    const attachmentsFull = yield select((state: RootState) =>
      attachFullItemsSelector(state, attachId),
    );

    if (attachments.length > ATTACHMENTS_COUNT_LIMIT) {
      return yield put(toastActions.setToastFail(t('photos_limit_error')));
    }

    const message: MessageModel = {
      id: messageId,
      fromId: currentUserId,
      toId: peerId,
      text,
      attachments: attachmentsFull,
      createdAt: Math.floor(Date.now() / 1000),
      isSending: true,
      isInbox: false,
      isUnread: true,
      price,
      priceWithoutDiscount,
      isHidden: true,
      currency,
      idempotencyKey: uuidv7(),
    };

    yield put(
      batchActions(
        attachActions.clear(attachId),
        imActions.setMessage({
          peerId,
          messageId: message.id,
          message,
        }),
      ),
    );

    const data = yield call(
      sendMessageRequest,
      `/im/${peerId}/send`,
      {
        text,
        attachments,
        randomId: message.id,
        price,
        priceWithoutDiscount,
        currency,
      },
      message.idempotencyKey,
    );

    yield call(finalizeSentMessage, peerId, message, data);

    if (mVerified) {
      yield call(statReachGoal, 'paid_message');
    }

    yield put(toastActions.hideToast());
    yield call(goBack);
    yield call(setTimeout, () => scrollToBottom('chat_history'), 50);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(imActions.removeMessage({ id: messageId, peerId }));
  }
}

function* loadTonRateWorker() {
  try {
    const { rate } = yield call(Api.get, '/rates/ton');

    yield put(imActions.setTonRate(rate));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* editWelcomeMessageWorker(action: EditWelcomeMessageAction) {
  const type = action.payload;
  try {
    const { welcomeMessage: welcomeMessageForms } = yield select(imSelector);

    const welcomeMessage = welcomeMessageForms[type];
    const { text, price, priceWithoutDiscount, isPaid, isEnabled } = welcomeMessage;

    const params: any = {
      text: '',
      type,
      price: 0,
      priceWithoutDiscount: 0,
      attachments: [],
      enabled: isEnabled,
    };
    if (isEnabled) {
      if (isPaid && (+price < 0.1 || !price)) {
        return yield put(
          toastActions.setToastFail(
            t('create_invoice_amount_to_small', {
              amount: formatCurrency(0.1, CurrencyEnum.EUR),
            }),
          ),
        );
      }

      const attachments = yield select((state: RootState) =>
        attachItemsSelector(state, 'welcome_message_' + type),
      );

      if (attachments === 'photo_uploading') {
        return yield put(toastActions.setToastFail(t('photo_uploading_err')));
      }

      if (!attachments.length && !text.length) {
        return yield put(toastActions.setToastFail(t('im_empty_error')));
      }

      params.text = text;
      params.price = +price;
      params.priceWithoutDiscount = +priceWithoutDiscount;
      params.attachments = attachments;

      if (!isPaid) {
        params.price = 0;
        params.priceWithoutDiscount = 0;
      }
    }

    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/im/welcome_message/save', params);
    yield put(toastActions.setToastSuccess());
    yield call(
      statReachGoal,
      isEnabled ? 'welcome_message_enable' : 'welcome_message_disable',
    );
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadBroadcastListsWorker() {
  try {
    const data = yield call(Api.post, '/lists/filter_new', {
      excludeLists: [],
      filters: [],
      lists: [],
      excludeBroadcastFilter: true,
    });

    yield put(imActions.setBroadcastList(data.lists));
    yield put(mainActions.setUsers(data.participants));
  } catch (e) {
    console.log(e.message);
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadBroadcastStatsWorker() {
  try {
    const { broadcasts, nextFrom } = yield call(Api.post, '/stats/broadcasts', {
      startFrom: '',
    });

    yield put(
      imActions.setBroadcastStats({
        isLoading: false,
        isLoadingMore: false,
        broadcasts,
        nextFrom,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMoreBroadcastStatsWorker(action: LoadBroadcastStats) {
  try {
    const { broadcasts, nextFrom } = yield call(Api.post, '/stats/broadcasts', {
      startFrom: action.payload,
    });

    yield put(
      imActions.setMoreBroadcastStats({
        broadcasts,
        nextFrom,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* unsendBroadcastWorker(action: UnsendBroadcastAction) {
  try {
    const id = action.payload;
    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/im/broadcast/${id}/rollback`, {
      startFrom: action.payload,
    });

    yield put(
      imActions.setUnsentBroadcast({
        id: action.payload,
        unsent: true,
      }),
    );

    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(
      imActions.setUnsentBroadcast({
        id: action.payload,
        unsent: false,
      }),
    );
    yield put(toastActions.setToastFail(e.message));
  }
}

function* broadcastFilterUserWorker() {
  yield put(imActions.filterBroadcastUsers());
}

function* broadcastLoadFilteredRecipientsWorker() {
  const { sendTo, exclude, appliedActiveFilters, appliedFilters } = (yield select(
    (state: RootState) => state.im.broadcast,
  )) as RootState['im']['broadcast'];

  const [subscribeDateFrom, subscribeDateTo] = appliedFilters.subscribeDate.map((date) =>
    Math.round(date.getTime() / 1000),
  );
  const [sponsorSubscribeDateFrom, sponsorSubscribeDateTo] =
    appliedFilters.sponsorSubscribeDate.map((date) => Math.round(date.getTime() / 1000));
  const [registrationDateFrom, registrationDateTo] = appliedFilters.registrationDate.map(
    (date) => Math.round(date.getTime() / 1000),
  );

  yield put(
    batchActions(
      imActions.setBroadcastRecipient([]),
      imActions.setIsRecipientsLoading(true),
      imActions.filterBroadcastUsers(),
    ),
  );
  const { participants, skip, total } = yield call(Api.post, '/lists/filter_new', {
    lists: sendTo,
    excludeLists: exclude,
    filters: [
      appliedActiveFilters[BroadcastFilterName.LANGUAGES] && {
        name: BroadcastFilterName.LANGUAGES,
        params: appliedFilters.languages,
      },
      appliedActiveFilters[BroadcastFilterName.SUBSCRIBE_DATE] && {
        name: BroadcastFilterName.SUBSCRIBE_DATE,
        params: { from: subscribeDateFrom, to: subscribeDateTo },
      },
      appliedActiveFilters[BroadcastFilterName.REGISTRATION_DATE] && {
        name: BroadcastFilterName.REGISTRATION_DATE,
        params: { from: registrationDateFrom, to: registrationDateTo },
      },
      appliedActiveFilters[BroadcastFilterName.SPONSOR_SUBSCRIBE_DATE] && {
        name: BroadcastFilterName.SPONSOR_SUBSCRIBE_DATE,
        params: { from: sponsorSubscribeDateFrom, to: sponsorSubscribeDateTo },
      },
    ].filter(Boolean),
  });

  yield put(
    batchActions(
      mainActions.setUsers(participants),
      imActions.sendBroadcastTotalRecipientsCount(total),
      imActions.setIsRecipientsLoading(false),
      imActions.setBroadcastRecipient(participants),
      imActions.setBroadcastSkipIds(skip),
      imActions.filterBroadcastUsers(),
    ),
  );
}

function* sendBroadcastMessageWorker() {
  try {
    const {
      broadcast: {
        text,
        price,
        priceWithoutDiscount,
        isPaid,
        sendTo,
        exclude,
        excludeUserIds,
        rollbackPrevious,
        filters,
        activeFilters,
        // skipUpToTopDialogs,
      },
    } = (yield select(imSelector)) as RootState['im'];

    const [subscribeDateFrom, subscribeDateTo] = filters.subscribeDate.map((date) =>
      Math.round(date.getTime() / 1000),
    );
    const [sponsorSubscribeDateFrom, sponsorSubscribeDateTo] =
      filters.sponsorSubscribeDate.map((date) => Math.round(date.getTime() / 1000));
    const [registrationDateFrom, registrationDateTo] = filters.registrationDate.map(
      (date) => Math.round(date.getTime() / 1000),
    );

    const params: any = {
      text: '',
      price: 0,
      priceWithoutDiscount: 0,
      rollbackPrevious,
      // skipUpToTopDialogs,
      attachments: [],
    };
    let isConfirmed = true;

    if (isPaid && (+price < 0.1 || !price)) {
      return yield put(
        toastActions.setToastFail(
          t('create_invoice_amount_to_small', {
            amount: formatCurrency(0.1, CurrencyEnum.EUR),
          }),
        ),
      );
    }

    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, 'broadcast_message'),
    );

    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (!attachments.length && !text.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    if (!attachments.length && text.length) {
      isConfirmed = yield call(_confirm, t('broadcast_no_content_confirm'));
    }

    if (isConfirmed) {
      params.text = text;
      params.price = +price;
      params.priceWithoutDiscount = +priceWithoutDiscount;
      params.attachments = attachments;
      params.lists = sendTo;
      params.exclude = excludeUserIds;
      params.excludeLists = exclude;
      params.randomId = --MsgId;
      params.filters = [
        activeFilters[BroadcastFilterName.LANGUAGES] && {
          name: BroadcastFilterName.LANGUAGES,
          params: filters.languages,
        },
        activeFilters[BroadcastFilterName.SUBSCRIBE_DATE] && {
          name: BroadcastFilterName.SUBSCRIBE_DATE,
          params: { from: subscribeDateFrom, to: subscribeDateTo },
        },
        activeFilters[BroadcastFilterName.REGISTRATION_DATE] && {
          name: BroadcastFilterName.REGISTRATION_DATE,
          params: { from: registrationDateFrom, to: registrationDateTo },
        },
        activeFilters[BroadcastFilterName.SPONSOR_SUBSCRIBE_DATE] && {
          name: BroadcastFilterName.SPONSOR_SUBSCRIBE_DATE,
          params: { from: sponsorSubscribeDateFrom, to: sponsorSubscribeDateTo },
        },
      ].filter(Boolean);

      if (!isPaid) {
        params.price = 0;
        params.priceWithoutDiscount = 0;
      }

      yield put(toastActions.setToastLoading());
      yield call(Api.post, '/im/broadcast/send_new', params);
      yield put(
        batchActions(
          toastActions.setToastSuccess(),
          imActions.resetBroadcast(),
          imActions.setBroadcastSendTo([]),
          imActions.setBroadcastExclude([]),
          attachActions.clear('broadcast_message'),
        ),
      );
      yield call(goTo, IM);
      yield call(statReachGoal, 'broadcast_send');
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* buyMessageWorker(action: BuyMessageAction) {
  const { message, peerId } = action.payload;
  const { users } = yield select(mainSelector);
  const user = users[peerId];

  let method, options;
  if (message.currency === CurrencyEnum.PLACE) {
    method = PayMethod.BALANCE;
    options = {};
  } else {
    const payRes = yield paySaga({
      amount: message.price || 0,
      title: t('buy_message_title'),
      description: t('buy_message_info', { price: message.price }),
      excludeFiat: !user.verified,
    });
    method = payRes.method;
    options = payRes.options;
  }

  if (method) {
    yield put(toastActions.setToastLoading());
  }

  if (method === PayMethod.BALANCE) {
    try {
      const { decryptKey } = yield call(Api.post, `/im/${message.id}/buy`, {
        skipAutoPay: !!options.skipAutoPay,
      });

      yield put(
        batchActions(
          imActions.setMessage({
            peerId,
            messageId: message.id,
            message: {
              ...message,
              isHidden: false,
              decryptKey,
            },
          }),
          toastActions.setToastSuccess(),
        ),
      );
    } catch (e) {
      if (e.code === 'insufficient_funds') {
        if (message.currency === CurrencyEnum.PLACE) {
          router.navigate(MINE);
        } else {
          yield call(showModal, 'top_up');
        }
      }
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.CARD) {
    try {
      const { status, token } = yield call(Api.post, `/pay/message/${message.id}`, {
        skipAutoPay: !!options.skipAutoPay,
      });

      if (status === 'sent_payment') {
        yield put(toastActions.setToastLoading(t('toast_payment_processing')));
      } else {
        yield put(toastActions.hideToast());
        // @ts-ignore
        yield call(router.navigate, PAY_FORM, { token }); // TODO
      }
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.CRYPTOPAY) {
    try {
      const { payUrl, invoiceId } = yield call(Api.post, `/cryptopay/message`, {
        messageId: message.id,
        currency: options.currency,
      });
      yield put(payActions.setPayUrl(payUrl));
      yield put(payActions.setInvoiceId(invoiceId));
      yield put(toastActions.hideToast());
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

function* sendTipsWorker(action: SendTipsAction) {
  const { toId, amount, id, currency } = action.payload;
  const { users } = yield select(mainSelector);
  const user = users[toId];

  const { method, options } = yield paySaga({
    amount: amount,
    title: t('send_tip_title'),
    description: t('send_tip_description', {
      amount: formatCurrency(amount, currency),
      name: user.firstName + ' ' + user.lastName,
    }),
    excludeFiat: !user.verified,
  });

  if (method) {
    yield put(toastActions.setToastLoading());
  }

  if (method === PayMethod.CARD) {
    try {
      const { status, token } = yield call(Api.post, `/pay/tips/${id}`, {
        skipAutoPay: options.skipAutoPay,
      });
      if (status === 'sent_payment') {
        yield put(
          batchActions(toastActions.setToastSuccess(), mainActions.setPopup(null)),
        );
        yield put(balanceActions.setIsTipsPaid(true));
      } else {
        yield put(batchActions(toastActions.hideToast(), mainActions.setPopup(null)));
        // @ts-ignore
        yield call(router.navigate, PAY_FORM, { token }, { replace: true });
      }
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.BALANCE) {
    try {
      yield put(toastActions.setToastLoading());

      const data = yield call(Api.post, `/im/tips/${id}/send`);

      yield put(
        imActions.setMessage({
          peerId: data.message.toId,
          messageId: data.message.id,
          message: data.message,
        }),
      );

      yield put(batchActions(toastActions.hideToast(), mainActions.setPopup(null)));
      yield call(setTimeout, () => scrollToBottom('chat_history'), 50);
      yield put(balanceActions.setIsTipsPaid(true));
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.CRYPTOPAY) {
    try {
      const { payUrl, invoiceId } = yield call(Api.post, `/cryptopay/tips`, {
        tipsId: id,
        currency: options.currency,
      });
      yield put(payActions.setPayUrl(payUrl));
      yield put(payActions.setInvoiceId(invoiceId));
      yield put(toastActions.hideToast());
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

function* sendInstantMessageWorker(action: SendInstantMessageAction) {
  try {
    const { peerId, text, asPeerId } = action.payload;

    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/im/${peerId}/send`, withAs({ text, randomId: 0 }, asPeerId));
    localStorage.setItem(`instantMessageSent_${peerId}`, `${Date.now()}`);
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* telegramRetryWorker(action: TelegramRetryAction) {
  try {
    const { messageId, peerId } = action.payload;

    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/im/telegram/${messageId}/resend`);
    yield put(toastActions.setToastSuccess());

    const history = yield select(imHistorySelector, peerId);
    const message = history[messageId];
    if (!message) {
      return;
    }

    yield put(
      batchActions(
        imActions.setMessage({
          peerId: peerId,
          messageId: messageId,
          message: {
            ...message,
            telegramStatus: MessageTelegramStatus.Sending,
          },
        }),
        toastActions.setToastSuccess(t('bought_message')),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* sendTypingWorker(action: SendTypingAction) {
  const { peerId, asPeerId } = action.payload;
  try {
    yield call(Api.post, `/im/${peerId}/typing`, asPeerId ? { asPeerId } : undefined);
  } catch (e) {}
}

const sendTypingTimeouts: { [key: number]: any } = {};
function* sendTypingWorkerThrottled(action: SendTypingAction) {
  const { peerId } = action.payload;
  const timeout = sendTypingTimeouts[peerId] || 0;
  const time = Date.now();
  if (timeout + 2000 < time) {
    sendTypingTimeouts[peerId] = time;
    yield sendTypingWorker(action);
  }
}

function* resetTypingWorker(action: SetTypingAction) {
  yield put(imActions.resetTyping(action.payload.peerId));
}

function* callLoopWorker(action: CallAction) {
  while (true) {
    try {
      const resp = yield call(Api.post, `/call/${action.payload.peerId}`, {
        description: action.payload.description,
      });
      const callId = resp?.callId || null;
      const fallbackStun = { urls: ['stun:stun.l.google.com:19302'] };
      const iceServers = resp?.iceServers?.length
        ? [...resp.iceServers, fallbackStun]
        : [fallbackStun];
      yield put(
        batchActions(
          imActions.setCallStatus(CallStatus.RINGING),
          imActions.setCallIceConfig({ callId, iceServers }),
        ),
      );
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
    yield delay(3000);
  }
}

function* callWorker(action: CallAction) {
  const callLoop = yield fork(callLoopWorker, action);
  yield take([
    imActions.setCallRemoteDescription,
    imActions.callCancel,
    imActions.hangup,
  ]);
  yield cancel(callLoop);
}

function* incomingCallWorker(action: IncomingCallAction) {
  const { status } = yield select(imCallSelector);
  if (status === null) {
    const { peerId, remoteDescription } = action.payload;
    try {
      const data = yield call(Api.get, `/profile/${peerId}`);
      yield call(handleProfileResult, peerId, data);
      yield put(
        batchActions(
          imActions.setCallStatus(CallStatus.RINGING),
          imActions.setCallType(CallType.INCOMING),
          imActions.setCallPeerId(peerId),
          imActions.setCallRemoteDescription(remoteDescription),
        ),
      );
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  } else {
    // TODO: Send answer with busy
  }
}

function* callSendAcceptWorker(action: CallSendAcceptAction) {
  const { peerId, status, callId } = yield select(imCallSelector);
  if (status === CallStatus.CONNECTING) {
    try {
      const body: { description: string; callId?: string } = {
        description: btoa(JSON.stringify(action.payload)),
      };
      if (callId) {
        body.callId = callId;
      }
      yield call(Api.post, `/call/${peerId}/accept`, body);
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

function* callRejectWorker() {
  const { peerId, callId } = yield select(imCallSelector);
  try {
    const body: { reason: number; callId?: string } = { reason: 1 };
    if (callId) {
      body.callId = callId;
    }
    yield call(Api.post, `/call/${peerId}/reject`, body);
    yield put(imActions.hangup());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* callCancelWorker(action: CallCancelAction) {
  const { callId } = yield select(imCallSelector);
  try {
    const body: { callId?: string } = {};
    if (callId) {
      body.callId = callId;
    }
    yield call(Api.post, `/call/${action.payload}/cancel`, body);
    yield put(imActions.hangup());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* hangupWorker() {
  yield put(imActions.hangup());
}

function* loadAvgInvoicePriceWorker(action: LoadAvgInvoicePriceAction) {
  try {
    const data = yield call(Api, `/im/${action.payload}/create_invoice_info`);
    yield put(imActions.setAvgInvoicePrice(data.avgPrice));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadWelcomeMessageWorker(action: LoadWelcomeMessageAction) {
  const type = action.payload;
  try {
    yield put(
      imActions.setWelcomeMessageLoading({
        type,
        loading: 'loading',
      }),
    );
    const data = yield call(Api.get, `/im/welcome_message/info?type=${type}`);
    yield put(
      batchActions(
        imActions.setWelcomeMessage({
          type,
          data: {
            loadingStatus: 'loaded',
            text: data.text,
            price: data.price > 0 ? data.price : '',
            priceWithoutDiscount:
              data.priceWithoutDiscount > 0 ? data.priceWithoutDiscount : '',
            isPaid: data.price > 0,
            isEnabled: !!data.enabled,
          },
        }),
        attachActions.initFromAttachments({
          pickerId: 'welcome_message_' + type,
          attachments: data.attachments,
        }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(
      imActions.setWelcomeMessageLoading({
        type,
        loading: 'failed',
      }),
    );
  }
}

function* telegramGetChatsWorker() {
  try {
    const { chooseChatTelegramSheetType } = yield select(imTelegramSelector);
    const data = yield call(Api.get, '/im/telegram/get_chats', {
      params: {
        type: chooseChatTelegramSheetType,
      },
    });
    yield put(imActions.setTelegramChats(data.chats));
  } catch (e) {
    yield put(imActions.setTelegramChats([]));
    yield put(toastActions.setToastFail(e.message));
  }
}

function* sendValentineDayStickerWorker(action: SendValentineDayStickerAction) {
  try {
    const peerId = action.payload;
    yield put(toastActions.setToastLoading());

    const data = yield call(Api.post, `/im/${peerId}/valentine_day_sticker/send`);
    yield put(
      batchActions(
        imActions.setMessage({
          peerId,
          messageId: data.message.id,
          message: data.message,
        }),
        toastActions.hideToast(),
      ),
    );

    yield call(goTo, CHAT, { peerId }, { replace: true });
    yield call(statReachGoal, 'valentine_day_sticker');
  } catch (e) {
    if (e.code === 'insufficient_funds') {
      yield put(
        balanceActions.setTopUpActive({
          topUpActive: true,
          requireTopUpAmount: 1,
        }),
      );
    } else if (e.code === 'access_token') {
      yield put(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
    }
    yield put(toastActions.setToastFail(e.message));
  }
}

function* clearBroadcastExcludeUserIdsWorker() {
  yield put(imActions.setBroadcastExcludeUserIds([]));
}

function* switchServiceAccountWorker(action: SetAsPeerIdAction) {
  const asPeerId = action.payload || undefined;
  yield put(imActions.loadDialogs(asPeerId ? { asPeerId } : undefined));
}

export function* imSaga() {
  yield all([
    takeLatest(imActions.loadServiceAccounts, loadServiceAccountsWorker),
    takeLatest(imActions.loadDialogs, loadDialogsWorker),
    takeLatest(
      [imActions.setAsPeerId, imActions.resetAsPeerId],
      switchServiceAccountWorker,
    ),
    takeLatest(imActions.loadMoreDialogs, loadMoreDialogsWorker),
    takeEvery(imActions.sendMessage, sendMessageWorker),
    takeEvery(imActions.retrySend, retrySendWorker),
    takeLatest(imActions.loadChat, loadChatWorker),
    takeLatest(imActions.loadMoreHistory, loadMoreHistoryWorker),
    takeLatest(imActions.readHistory, readHistoryWorker),
    takeLatest(imActions.loadWrite, loadWriteWorker),
    takeLatest(imActions.deleteDialog, deleteDialogWorker),
    takeLatest(imActions.deleteMessage, deleteMessageWorker),
    takeEvery(imActions.deleteUnsentMessage, deleteUnsentMessageWorker),
    takeLatest(imActions.reportMessage, reportMessageWorker),
    takeLatest(imActions.createInvoice, createInvoiceWorker),
    takeLatest(imActions.createTelegramInvoice, createTelegramInvoiceWorker),
    takeLatest(imActions.telegramGetChats, telegramGetChatsWorker),
    takeLatest(imActions.buyMessage, buyMessageWorker),
    takeLatest(imActions.sendTips, sendTipsWorker),
    takeLatest(imActions.loadTonRate, loadTonRateWorker),
    takeLatest(imActions.editWelcomeMessage, editWelcomeMessageWorker),
    takeLatest(imActions.loadBroadcastLists, loadBroadcastListsWorker),
    takeLatest(imActions.loadBroadcastStats, loadBroadcastStatsWorker),
    takeLatest(imActions.loadMoreBroadcastStats, loadMoreBroadcastStatsWorker),
    takeLatest(imActions.loadWelcomeMessage, loadWelcomeMessageWorker),
    takeLatest(imActions.sendBroadcastMessage, sendBroadcastMessageWorker),
    takeEvery(
      [imActions.setBroadcastExclude, imActions.setBroadcastSendTo],
      clearBroadcastExcludeUserIdsWorker,
    ),
    takeLatest(
      [
        imActions.loadBroadcastLists,
        imActions.setBroadcastSendTo,
        imActions.setBroadcastExclude,
        imActions.applyBroadcastFilters,
        // imActions.setBroadcastFilterLanguage,
        // imActions.setBroadcastFilterSubscribeDate,
        // imActions.setBroadcastFilterRegistrationDate,
        // imActions.setBroadcastFilterActive,
      ],
      broadcastLoadFilteredRecipientsWorker,
    ),
    takeLatest(imActions.resetBroadcastSearchText, broadcastFilterUserWorker),
    takeLatest(imActions.unsendBroadcast, unsendBroadcastWorker),
    takeLatest(imActions.sendInstantMessage, sendInstantMessageWorker),
    takeLatest(imActions.sendInstantMessage, sendInstantMessageWorker),
    takeLatest(imActions.telegramRetry, telegramRetryWorker),
    takeLatest(imActions.incomingCall, incomingCallWorker),
    takeLatest(imActions.call, callWorker),
    takeLatest(imActions.callSendAccept, callSendAcceptWorker),
    takeLatest(imActions.callReject, callRejectWorker),
    takeLatest(imActions.callCancel, callCancelWorker),
    takeLatest([imActions.closeConnectionCall, imActions.callReject], hangupWorker),
    debounce(250, imActions.setBroadcastSearchText, broadcastFilterUserWorker),
    takeLatest(imActions.loadAvgInvoicePrice, loadAvgInvoicePriceWorker),
    debounce(3000, imActions.setTyping, resetTypingWorker),
    takeLatest(imActions.sendTyping, sendTypingWorkerThrottled),
    takeLatest(imActions.sendValentineDaySticker, sendValentineDayStickerWorker),
  ]);
}
